import { useCallback, useEffect, useState } from "react";
import { Search, Eye, Trash2, MessageCircle, ArrowUpDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import api, { formatApiError, tiktokHandle } from "../../lib/api";

export const STATUS_LABELS = {
  new: "Yeni",
  reviewing: "İnceleniyor",
  contacted: "İletişime Geçildi",
  accepted: "Kabul Edildi",
  rejected: "Reddedildi",
};

const STATUS_COLORS = {
  new: "text-cyan-300 bg-cyan-400/10 border-cyan-400/25",
  reviewing: "text-violet-300 bg-violet-400/10 border-violet-400/25",
  contacted: "text-blue-300 bg-blue-400/10 border-blue-400/25",
  accepted: "text-emerald-300 bg-emerald-400/10 border-emerald-400/25",
  rejected: "text-red-300 bg-red-400/10 border-red-400/25",
};

export function TikTokLink({ username, testid }) {
  const handle = tiktokHandle(username);
  if (!handle) return <span className="text-xs text-[#64748B]">—</span>;
  return (
    <a
      href={`https://www.tiktok.com/@${handle}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-300 hover:text-cyan-100 hover:underline transition-colors"
    >
      @{handle}
      <ExternalLink size={11} strokeWidth={1.5} />
    </a>
  );
}

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [order, setOrder] = useState("desc");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const fetchApps = useCallback(() => {
    setLoading(true);
    api
      .get("/admin/applications", { params: { search, status, order } })
      .then((r) => setApps(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [search, status, order]);

  useEffect(() => {
    const t = setTimeout(fetchApps, 300);
    return () => clearTimeout(t);
  }, [fetchApps]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status: newStatus });
      toast.success("Durum güncellendi");
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status: newStatus }));
      window.dispatchEvent(new Event("applications-updated"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveNote = async () => {
    try {
      await api.patch(`/admin/applications/${selected.id}`, { admin_note: note });
      toast.success("Not kaydedildi");
      setApps((prev) => prev.map((a) => (a.id === selected.id ? { ...a, admin_note: note } : a)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/applications/${deleteId}`);
      toast.success("Başvuru silindi");
      setApps((prev) => prev.filter((a) => a.id !== deleteId));
      window.dispatchEvent(new Event("applications-updated"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  const waLink = (phone) => `https://wa.me/${phone.replace(/[^0-9]/g, "")}`;
  const selectCls = "bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-cyan-400/60";

  return (
    <div data-testid="admin-applications-page">
      <h1 className="font-display font-700 text-xl text-white mb-1">Başvurular</h1>
      <p className="text-sm text-[#64748B] mb-8">Ajans başvurularını yönetin</p>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input
            data-testid="applications-search-input"
            className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-xs text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60"
            placeholder="Ad, telefon, e-posta veya kullanıcı adı ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select data-testid="applications-status-filter" className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Tüm Durumlar</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <button
          data-testid="applications-sort-toggle"
          onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))}
          className="inline-flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white hover:border-cyan-400/40 transition-colors"
        >
          <ArrowUpDown size={14} />
          {order === "desc" ? "En Yeni Önce" : "En Eski Önce"}
        </button>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-[#0A0E17] overflow-x-auto">
        <table className="w-full min-w-[820px] text-left" data-testid="applications-table">
          <thead>
            <tr className="border-b border-white/[0.06]">
              {["Ad Soyad", "Telefon", "E-posta", "TikTok", "Tarih", "Durum", "İşlemler"].map((h) => (
                <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-[#64748B]">Yükleniyor...</td></tr>
            ) : apps.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-[#64748B]" data-testid="applications-empty">Başvuru bulunamadı.</td></tr>
            ) : (
              apps.map((a) => (
                <tr key={a.id} className="hover:bg-white/[0.02]" data-testid={`application-row-${a.id}`}>
                  <td className="px-5 py-4 text-sm font-semibold text-white">{a.full_name}</td>
                  <td className="px-5 py-4 text-xs text-[#A0ABC0]">{a.phone}</td>
                  <td className="px-5 py-4 text-xs text-[#A0ABC0]">{a.email || "—"}</td>
                  <td className="px-5 py-4">
                    <TikTokLink username={a.tiktok_username} testid={`application-tiktok-link-${a.id}`} />
                  </td>
                  <td className="px-5 py-4 text-xs text-[#A0ABC0]">{new Date(a.created_at).toLocaleDateString("tr-TR")}</td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`application-status-select-${a.id}`}
                      value={a.status}
                      onChange={(e) => updateStatus(a.id, e.target.value)}
                      className={`text-[11px] font-semibold border rounded-full px-2.5 py-1.5 outline-none bg-transparent cursor-pointer ${STATUS_COLORS[a.status]}`}
                    >
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k} className="bg-[#0A0E17] text-white">{v}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`application-view-${a.id}`}
                        onClick={() => { setSelected(a); setNote(a.admin_note || ""); }}
                        className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors"
                        aria-label="Detay"
                      >
                        <Eye size={14} />
                      </button>
                      <a
                        data-testid={`application-whatsapp-${a.id}`}
                        href={waLink(a.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-emerald-300 hover:border-emerald-400/40 transition-colors"
                        aria-label="WhatsApp"
                      >
                        <MessageCircle size={14} />
                      </a>
                      <button
                        data-testid={`application-delete-${a.id}`}
                        onClick={() => setDeleteId(a.id)}
                        className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-red-300 hover:border-red-400/40 transition-colors"
                        aria-label="Sil"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0A0E17] border-white/10 text-white max-w-lg" data-testid="application-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">Başvuru Detayı</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-[11px] text-[#64748B] mb-1">Ad Soyad</p><p className="text-white font-semibold" data-testid="detail-fullname">{selected.full_name}</p></div>
                <div><p className="text-[11px] text-[#64748B] mb-1">Telefon</p><p className="text-white">{selected.phone}</p></div>
                <div><p className="text-[11px] text-[#64748B] mb-1">E-posta</p><p className="text-white">{selected.email || "—"}</p></div>
                <div><p className="text-[11px] text-[#64748B] mb-1">TikTok</p><TikTokLink username={selected.tiktok_username} testid="detail-tiktok-link" /></div>
                <div><p className="text-[11px] text-[#64748B] mb-1">Başvuru Tarihi</p><p className="text-white">{new Date(selected.created_at).toLocaleString("tr-TR")}</p></div>
                <div><p className="text-[11px] text-[#64748B] mb-1">Durum</p><p className="text-white">{STATUS_LABELS[selected.status]}</p></div>
              </div>
              <div>
                <p className="text-[11px] text-[#64748B] mb-1">Neden bizi tercih ediyor?</p>
                <p className="text-[#A0ABC0] bg-black/30 rounded-xl p-3.5 text-xs leading-relaxed">{selected.why_us || "—"}</p>
              </div>
              <div>
                <p className="text-[11px] text-[#64748B] mb-1.5">Admin Notu</p>
                <textarea
                  data-testid="application-note-input"
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-3 text-xs text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 resize-none"
                  placeholder="Bu başvuru hakkında not ekleyin..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="flex gap-3 mt-3">
                  <button onClick={saveNote} data-testid="application-note-save" className="btn-glow flex-1 text-xs font-semibold text-white py-2.5 rounded-xl">
                    Notu Kaydet
                  </button>
                  <a
                    href={waLink(selected.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="application-detail-whatsapp"
                    className="flex-1 text-center text-xs font-semibold text-emerald-300 border border-emerald-400/30 rounded-xl py-2.5 hover:bg-emerald-400/10 transition-colors"
                  >
                    WhatsApp'tan Ulaş
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0A0E17] border-white/10 text-white" data-testid="application-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Başvuruyu Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0ABC0]">Bu başvuruyu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="application-delete-cancel" className="bg-transparent border-white/10 text-white hover:bg-white/5">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="application-delete-confirm" onClick={remove} className="bg-red-500 hover:bg-red-600 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
